import { Typography } from '@psl/components/typography';
import { config } from '@psl/functions/config';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import * as WebBrowser from 'expo-web-browser';
import { memo, useEffect, useReducer } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import { css } from 'styled-components';
import { Notification } from '../components/snackbar/Notification';
import { presentSnackbar } from '../components/snackbar/Snackbar';
import { useIsMobileLayout } from '../components/styles/responsive';
import qiao from '../functions/qiao';
import { safeMarkDevFlag } from '../utils/safeMarkDevFlag';
import { parseVersionPlain } from '../utils/version';
import * as version from '../version.json';

export function versionText(hashLength = 3) {
  const nativeAppVersion =
    Platform.OS === 'web'
      ? config.version
      : Application.nativeApplicationVersion!;
  return `${nativeAppVersion} (${config.env}-${
    version.code
  }-${version.current.slice(0, hashLength)})`;
}

const mobileCss = css`
  position: absolute;
  top: 3px;
  left: 0;
  right: 0;
  align-items: center;
`;

const deskTopCss = css`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const Content = () => {
  const isMobile = useIsMobileLayout();
  const [show, toggle] = useReducer(s => !s, true);
  if (!show) {
    return null;
  }
  return (
    <TouchableOpacity
      css={(isMobile ? mobileCss : deskTopCss) as any}
      onPress={toggle}
    >
      <View
        css={`
          padding: 0 10px;
          height: 24px;
          border-radius: 12px;
          background-color: white;
          justify-content: center;
        `}
      >
        <Typography.Text
          maxFontSizeMultiplier={1}
          medium
          size={13}
          color="black"
        >
          {versionText()}
        </Typography.Text>
      </View>
    </TouchableOpacity>
  );
};

export const VersionText = memo(() => {
  if ((config.isProd && !qiao.isInTestFlight) || __DEV__) {
    return null;
  }
  return <Content />;
});

// https://www.notion.so/popshoplive/What-s-New-3daaa41f1f6547a3ad4f0b9dcbd58f8e
// export const whatsNewLink = 'https://whatsnew.popshop.live';
export const whatsNewLink = 'https://outer-gamefowl.super.site/';

const kShowInDev = safeMarkDevFlag(false);

const presentNewVersion = (options = { delay: 1000, dismiss: 10000 }) => {
  presentSnackbar({
    ...options,
    children: (
      <Notification
        {...options}
        title="Latest Updates"
        closable
        actions={[
          {
            title: "See What's new?",
            onPress: async () => {
              await WebBrowser.openBrowserAsync(whatsNewLink, {
                windowFeatures: { width: 1024, height: 700 },
              });
            },
          },
        ]}
      />
    ),
  });
};

export const useTrackVersionUpgrade = () => {
  useEffect(() => {
    if (__DEV__) {
      if (kShowInDev) {
        presentNewVersion({
          delay: 0,
          dismiss: 1000000,
        });
        return;
      }
    }

    (async () => {
      const key = 'com.popshop.app.version';

      const localVersion = await AsyncStorage.getItem(key);

      if (!localVersion || localVersion === config.version) {
        if (!localVersion) {
          await AsyncStorage.setItem(key, config.version);
        }
        return;
      }

      await AsyncStorage.setItem(key, config.version);

      // skip patch version upgrade
      const localVersionInfo = parseVersionPlain(localVersion);
      const versionInfo = parseVersionPlain(config.version);
      if (
        localVersionInfo?.major === versionInfo?.major &&
        localVersionInfo?.minor === versionInfo?.minor
      ) {
        return;
      }

      // otherwise, show new version banner
      presentNewVersion();
    })().catch(() => null);
  }, []);
};
